import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { SalePriceFields } from '@/shop/sale';

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface CharacterItem extends SalePriceFields {
  id: string;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  idleUrl: string | null;
  climbUrl: string | null;
  fallUrl: string | null;
  winUrl: string | null;
  idleFrames: string[] | null;
  climbFrames: string[] | null;
  fallFrames: string[] | null;
  winFrames: string[] | null;
  stompPower: number;
  tint: string | null;
  /** Admin-flagged default starter (auto-granted to new users). */
  isStarter?: boolean;
  owned: boolean;
  equipped: boolean;
  /** null = never trialed; 0 = trialed & expired; >0 = active trial uses left. */
  trialUsesLeft?: number | null;
}

export interface CharacterListResponse {
  equippedId: string | null;
  coins: number;
  gems: number;
  items: CharacterItem[];
}

const BASE = SERVER_URL;

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(detail || `Characters API ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const characterApi = {
  list: () => req<CharacterListResponse>('GET', '/characters/'),
  buy: (id: string, currency: 'coins' | 'gems') =>
    req<{ coins: number; gems: number }>('POST', `/characters/${id}/buy`, { currency }),
  equip: (id: string) => req<{ equippedId: string }>('POST', `/characters/${id}/equip`),
};

export const RARITY_COLOR: Record<Rarity, string> = {
  common: '#9aa4b8',
  rare: '#3B82F6',
  epic: '#9B5DE5',
  legendary: '#F4C724',
};
